import { Typography } from '@mui/joy';
import { Warning } from '@phosphor-icons/react';
import { times, uniqueId } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ImportHeader } from 'bom/enums';
import { importService } from 'bom/services';
import { useBomPartsImportContext } from 'bom/states';
import { AgGridServerTable, Button, CheckboxInput } from 'core/components';
import { AgGridServerTableProvider } from 'core/states';
import { AgGridDataRow, AgGridGetRowsParams, GetManyResponse } from 'core/types';
import { Color, Level, Variant } from 'theme/enums';

import HeaderComponent from './HeaderComponent';
import { SelectHeaderRowButton } from './SelectHeaderRow';
import { ActionsWrapper, tableStyle } from './style';
import './style.css';

const PAGE_SIZE = 30;

const RowMapping = () => {
  const { t } = useTranslation();

  const {
    partImportId,
    importData,
    setImportCreateData,
    headerRowIndex,
    setHeaderRowIndex,
    mergeDuplicates,
    setMergeDuplicates,
    isQuoteImport,
    ignoreEmptyQuantities,
    setIgnoreEmptyQuantities,
    numberOfColumns,
    setHeaderRow,
    clearRecognizedHeaders,
    columnsMapping,
    setColumnsMapping,
  } = useBomPartsImportContext();

  const isVendorQuoteNumberMapped = columnsMapping?.some(
    (column) => column.isMapped && column.field === ImportHeader.VENDOR_QUOTE_NUMBER
  );

  const defaultColDef = useMemo(
    () => ({
      wrapHeaderText: true,
      autoHeaderHeight: true,
      filter: false,
      floatingFilter: false,
      sortable: false,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      suppressHeaderContextMenu: true,
      headerComponent: HeaderComponent,
      minWidth: 100,
      maxWidth: 200,
      enableRowGroup: false,
    }),
    []
  );

  const columnDefs = useMemo(() => {
    return times(numberOfColumns, (index) => ({
      colId: index.toString(),
      headerName: '',
      field: `col${index}`,
    }));
  }, [numberOfColumns]);

  const getRows = useCallback(
    async ({ page, pageSize }: AgGridGetRowsParams) => {
      const { records, count, headerRow } = await importService.getImportPreviewRows({
        partImport: partImportId,
        includeEmptyRows: 0,
        includeHeaderRow: 0,
        headerRowIndex,
        page,
        pageSize,
      });

      if (1 === page && headerRowIndex > -1) {
        setHeaderRow(headerRow);
      }

      const recordsMapped = records?.map((rowData: string[]) => {
        const row = rowData?.reduce((rowMapped, value, index) => {
          rowMapped[`col${index}`] = value;

          return rowMapped;
        }, {} as AgGridDataRow);

        row.id = Number(uniqueId());

        return row;
      });

      return Promise.resolve({
        records: recordsMapped,
        count,
      } as GetManyResponse<AgGridDataRow>);
    },
    [headerRowIndex, partImportId, setHeaderRow]
  );

  const onResetChanges = () => setImportCreateData(importData);

  const onClearHeaders = () => {
    clearRecognizedHeaders();
    setColumnsMapping((prev) =>
      prev.map((column) => ({
        ...column,
        field: column?.initialField || `col${column.colIndex}`,
        isMapped: false,
      }))
    );
  };

  const onSelectHeaderRowClick = (headerRowIndex: number) => {
    onClearHeaders();
    setHeaderRowIndex(headerRowIndex);
  };

  return (
    <AgGridServerTableProvider
      tableId="importPreviewRowsTable"
      entityType="Line"
      defaultColDef={defaultColDef}
      columnDefs={columnDefs}
      getRows={getRows}
      getRowId={({ data }) => data?.id?.toString()}
      pageSize={PAGE_SIZE}
      sideBar={{}}
      autoSizeStrategy={{
        type: 'fitCellContents',
        skipHeader: true,
      }}
      hasQuickSearch={false}
      headerHeight={120}
      hasRowSelection={false}
    >
      {isVendorQuoteNumberMapped && (
        <Typography
          color={Color.WARNING}
          level={Level.TITLE_SM}
          startDecorator={<Warning weight="fill" />}
          sx={{ marginBottom: 1 }}
        >
          {t('warningImportVendorQuoteNumber')}
        </Typography>
      )}

      <ActionsWrapper>
        <CheckboxInput
          label={isQuoteImport ? t('labelCombineDuplicateRows') : t('labelMergeDuplicates')}
          value={mergeDuplicates}
          onChange={() => setMergeDuplicates((prev) => !prev)}
          sx={{ zIndex: 1 }}
        />

        <CheckboxInput
          label={t('labelIgnoreEmptyQuantities')}
          value={ignoreEmptyQuantities}
          onChange={() => setIgnoreEmptyQuantities((prev) => !prev)}
          sx={{ zIndex: 1 }}
        />

        <Button
          text={t('buttonLabelResetChanges')}
          color={Color.NEUTRAL}
          variant={Variant.OUTLINED}
          onClick={onResetChanges}
          sx={{ zIndex: 1 }}
        />

        <SelectHeaderRowButton
          initialHeaderRowIndex={headerRowIndex}
          onSelectHeaderRowClick={onSelectHeaderRowClick}
        />

        <Button
          text={t('buttonLabelClearHeaders')}
          variant={Variant.SOFT}
          onClick={onClearHeaders}
          sx={{ zIndex: 1 }}
        />
      </ActionsWrapper>

      <AgGridServerTable wrapperStyle={tableStyle} />
    </AgGridServerTableProvider>
  );
};

export default RowMapping;
